import { Component } from '@angular/core';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';
import { PARTNER_ROLES, SubriseUserDetails, UserRole } from './core/models/users/subrise-user.model';
import { RouteNames } from './core/constants/route-names';
import { UserService } from './core/store/features/user.service';
import { PartnerUserService } from './core/services/partner-user.service';
import { PartnerUserApiService } from './core/services/api/partner-user-api.service';
import { CustomerService } from './core/store/features/customer.service';
import { VoiceService } from './core/store/features/voice.service';
import { AuthService } from './core/services/auth.service';
import { VersionCheckService } from './core/services/version-check.service';
import { BehaviorSubject, catchError, filter, finalize, from, map, mergeMap, Observable, of, switchMap, tap, throwError, toArray } from 'rxjs';
import { CategorizationService } from './core/store/features/categorization.service';
import { AccentService } from './core/store/features/accent.service';
import { CountryService } from './core/store/features/country.service';
import { LanguageService } from './core/store/features/language.service';
import { PlaceholderGroupService } from './core/store/features/placeholder-group.service';
import { PlaceholderService } from './core/store/features/placeholder.service';
import { BuildingBlockTemplateService } from './core/store/features/building-block-template.service';
import { BuildingBlockTagService } from './core/store/features/building-block-tag.service';
import { SessionTemplateService } from './core/store/features/session-template.service';
import { SmartSurveyService } from './core/store/features/smart-survey.service';
import { SmartUserProgramService } from './core/store/features/smart-user-program.service';
import { SessionService } from './core/store/features/session.service';
import { SeriesTemplateService } from './core/store/features/series-template.service';
import { ProgramTemplateService } from './core/store/features/program-template.service';
import { TherapeuticDomainService } from './core/store/features/therapeutic-domain.service';
import { MessageTemplateService } from './core/store/features/message-template.service';
import { LLMInstructionService } from './core/store/features/llm-instruction.service';
import { SessionCategoryService } from './core/store/features/session-category.service';
import { AdminStatsService } from './core/store/features/admin-stats.service';
import { PartnerDashboardService } from './core/store/features/partner-dashboard.service';
import { environment } from '@beyou/environments/environment';
import { SessionLengthStatsService } from './core/store/features/session-length-stats.service';
import { rolesHavePortalAccess } from './core/utils/auth-claims.utils';
import { Permission } from './core/models/users/permission.model';
import { PermissionsService } from './core/services/permissions.service';
import { UserRolesService } from './core/services/user-roles.service';
import { PracticeContextService } from './core/services/practice-context.service';
import { StarterOverviewService } from './features/dashboard/services/starter-overview.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
  title = 'beyou-app';

  isSidemenuOpen = false;
  private isLoading = new BehaviorSubject<boolean>(true);
  public readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();
  private isLocalHost = window.location.hostname === 'localhost';
  private isAuthenticated = false; // Track authentication state
  private isBootstrapping = false; // A bootstrap() run is still in flight
  /** True from NavigationStart until the router settles; a redirect keeps it up. */
  private isNavigationPending = false;
  /** A request to hide the loader that is waiting for the router to settle. */
  private isLoaderReleaseDeferred = false;
  private roles: UserRole[] = [];
  readonly isProduction = environment.production;
  private readonly publicRoutePrefixes = [
    `/${RouteNames.PUBLIC_PLANS_ROUTE}`,
    `/${RouteNames.REGISTER_ROUTE}`,
    `/${RouteNames.PUBLIC_REFERRAL_ROUTE}/`,
    `/${RouteNames.PUBLIC_PARTNER_INVITE_ROUTE}/`,
    `/${RouteNames.PUBLIC_PATIENT_INVITE_ROUTE}`,
  ];
  private readonly chromelessShellRoutePrefixes = [
    `/${RouteNames.USER_ACCOUNT_ROUTE}/${RouteNames.USER_ACCOUNT_COMPLETE_PROFILE_SEGMENT}`,
  ];
  /**
   * How many back-office loads may be in flight at once. Firing all of them together saturates
   * the browser's connection pool and the API, and the slowest ones then time out; a small
   * window keeps every request inside its budget at the cost of a little wall-clock time.
   */
  private static readonly BACK_OFFICE_LOAD_CONCURRENCY = 4;

  /**
   * Back-office store loads, each paired with the permission that unlocks the feature it feeds.
   * These are thunks rather than observables on purpose: a store's `load()` flips its loading
   * flag the moment it is called, so an unauthorized entry must never be invoked at all, and a
   * queued one must not look like it is loading before its turn comes up.
   */
  private readonly backOfficeLoaders: ReadonlyArray<{ permission: Permission; load: () => Observable<unknown> }> = [
    { permission: Permission.AnalyticsView, load: () => this.adminStatsService.load() },
    { permission: Permission.CustomersManage, load: () => this.customerService.load() },
    { permission: Permission.LanguagesView, load: () => this.languageService.load() },
    { permission: Permission.PortalAccess, load: () => this.categorizationService.load() },
    { permission: Permission.AccentsView, load: () => this.accentService.load() },
    { permission: Permission.CountriesView, load: () => this.countryService.load() },
    { permission: Permission.VoicesView, load: () => this.voiceService.load() },
    { permission: Permission.ConfigManage, load: () => this.therapeuticDomainService.load() },
    { permission: Permission.SessionCategoriesView, load: () => this.sessionCategoryService.load() },
    { permission: Permission.ConfigManage, load: () => this.loadPlaceholders() },
    { permission: Permission.ConfigManage, load: () => this.buildingBlockTagService.load() },
    { permission: Permission.ConfigManage, load: () => this.smartSurveyService.load() },
    { permission: Permission.ConfigManage, load: () => this.smartUserProgramService.load() },
    { permission: Permission.ConfigManage, load: () => this.llmInstructionService.load() },
    { permission: Permission.ConfigManage, load: () => this.llmInstructionService.loadCodes() },
    { permission: Permission.ContentManage, load: () => this.buildingBlockTemplateService.load() },
    { permission: Permission.SessionTemplatesView, load: () => this.sessionTemplateService.load() },
    { permission: Permission.ContentManage, load: () => this.sessionLengthStatsService.load() },
    { permission: Permission.ContentManage, load: () => this.seriesTemplateService.load() },
    { permission: Permission.ContentManage, load: () => this.programTemplateService.load() },
    { permission: Permission.MessagingManage, load: () => this.messageTemplateService.load() },
  ];

  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly partnerUserService: PartnerUserService,
    private readonly partnerUserApiService: PartnerUserApiService,
    private readonly adminStatsService: AdminStatsService,
    private readonly partnerDashboardService: PartnerDashboardService,
    private readonly sessionLengthStatsService: SessionLengthStatsService,
    private readonly customerService: CustomerService,
    private readonly categorizationService: CategorizationService,
    private readonly accentService: AccentService,
    private readonly countryService: CountryService,
    private readonly languageService: LanguageService,
    private readonly voiceService: VoiceService,
    private readonly placeholderGroupService: PlaceholderGroupService,
    private readonly placeholderService: PlaceholderService,
    private readonly buildingBlockTemplateService: BuildingBlockTemplateService,
    private readonly buildingBlockTagService: BuildingBlockTagService,
    private readonly sessionTemplateService: SessionTemplateService,
    private readonly smartSurveyService: SmartSurveyService,
    private readonly smartUserProgramService: SmartUserProgramService,
    private readonly sessionService: SessionService,
    private readonly seriesTemplateService: SeriesTemplateService,
    private readonly programTemplateService: ProgramTemplateService,
    private readonly therapeuticDomainService: TherapeuticDomainService,
    private readonly sessionCategoryService: SessionCategoryService,
    private readonly messageTemplateService: MessageTemplateService,
    private readonly llmInstructionService: LLMInstructionService, 
    // Injected for its side effects only: constructing it at boot starts the ID-token refresh
    // interval, rather than leaving it to whenever the first HTTP request hits the interceptor.
    private readonly auth: AuthService,
    private readonly permissionsService: PermissionsService,
    private readonly userRolesService: UserRolesService,
    private readonly practiceContextService: PracticeContextService,
    // Injected for its side effect: it works out the dashboard's starter answer as soon as the
    // viewer's ties are known, so the dashboard has it ready instead of loading on every visit.
    _starterOverviewService: StarterOverviewService,
    private readonly versionCheck: VersionCheckService
  ) {
    console.log(`App running in ${this.isProduction ? 'production' : 'development'} mode`);

    this.applyShellForUrl(this.router.url);

    // Started in every environment: presence of version.json (i.e. a deployed
    // build) is what gates display/update detection, not the production flag —
    // staging and development deploys both run with production: false.
    this.versionCheck.start();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isNavigationPending = true;
      } else if (event instanceof NavigationEnd) {
        this.settleNavigation();
        this.applyShellForUrl(event.urlAfterRedirects);
      } else if (event instanceof NavigationSkipped || event instanceof NavigationError) {
        this.settleNavigation();
      } else if (event instanceof NavigationCancel && !this.isHandingOver(event.code)) {
        this.settleNavigation();
      }
    });

    this.loadData();
  }
  
  get showSideMenu(): boolean {
    if (!this.isSidemenuOpen) {
      return false;
    }
    return !this.isLoading.value;
  }

  get showVersionInfo(): boolean {
    return this.isAdmin;
  }

  set showSideMenu(value: boolean) {
    console.warn('showSideMenu is read-only');
  }

  get showHelpButton(): boolean {
    return this.isAdmin;
  }

  private get isAdmin(): boolean {
    return this.roles.includes(UserRole.Admin);
  }

  private applyShellForUrl(url: string): void {
    const chromeless = this.isChromelessRoute(url);
    this.isSidemenuOpen = !chromeless;
  }

  private isChromelessRoute(url: string): boolean {
    const route = url.split('#')[0] ?? url;
    if (this.isAuthChromelessRoute(route)) {
      return true;
    }

    if (this.publicRoutePrefixes.some((prefix) => route.startsWith(prefix))) {
      return true;
    }

    return this.chromelessShellRoutePrefixes.some(
      (prefix) =>
        route === prefix || route.startsWith(`${prefix}?`) || route.startsWith(`${prefix}/`)
    );
  }

  private isAuthChromelessRoute(url: string): boolean {
    const authBase = `/${RouteNames.AUTH_ROUTE}`;
    const logoutPath = `${authBase}/logout`;

    if (url === logoutPath || url.startsWith(`${logoutPath}?`) || url.startsWith(`${logoutPath}/`)) {
      return false;
    }

    return url === authBase || url.startsWith(`${authBase}/`) || url.startsWith(`${authBase}?`);
  }

  private async loadData(): Promise<void> {

    // `roles$` holds `null` until the ID token has been read, and goes back to it when another
    // Firebase user signs in: the previous user's data goes, and the new one bootstraps afresh.
    this.userRolesService.roles$.pipe(
      tap(roles => {
        if (roles === null && this.isAuthenticated) {
          this.isAuthenticated = false;
          this.clearAllData();
        }
      }),
      filter((roles): roles is UserRole[] => roles !== null),
      tap(roles => {
        console.debug('User roles resolved', { roles });
        if(this.isLocalHost){
          // Not waiting for all data to load when developing locally
          this.stopLoading();
        }
        
      }),
    ).subscribe(
      async roles => {
        const wasAuthenticated = this.isAuthenticated;
        const isNowAuthenticated = rolesHavePortalAccess(roles);
        
        // Only proceed if authentication state has changed. `roles$` re-emits the same roles on
        // every guarded navigation (see `UserRolesService.resolveCurrentRoles`), so this branch
        // runs constantly — it must not clear the loader while a bootstrap is still in flight.
        if (wasAuthenticated === isNowAuthenticated) {
          console.debug('Authentication state did not change, skipping data load');
          if (!this.isBootstrapping) {
            this.stopLoading();
          }
          return;
        }
        
        // Update authentication state
        this.isAuthenticated = isNowAuthenticated;
        this.roles = roles;

        if (isNowAuthenticated) {
          // Signing in happens long after the initial `false` emission cleared the loader, so
          // raise it again for the duration of the bootstrap instead of assuming it is still up.
          this.isBootstrapping = true;
          this.isLoading.next(true);
          this.bootstrap(this.roles)
            .pipe(
              catchError(error => {
                console.error('Error occurred:', error);
                return throwError(() => error);
              }),
              finalize(() => {
                console.log('Data loaded');
                this.isBootstrapping = false;
                this.stopLoading();
              })
            )
            .subscribe();

        } else {
          // User became unauthenticated - clear data
          console.warn('User became unauthenticated, clearing all data');
          this.clearAllData();
          this.stopLoading();
        }
      }
    );
  }

  /** A cancel that hands over to another navigation is not the router settling. */
  private isHandingOver(code: NavigationCancellationCode | undefined): boolean {
    return code === NavigationCancellationCode.Redirect
      || code === NavigationCancellationCode.SupersededByNewNavigation;
  }

  private settleNavigation(): void {
    this.isNavigationPending = false;
    if (!this.isLoaderReleaseDeferred) {
      return;
    }
    this.isLoaderReleaseDeferred = false;
    // Still bootstrapping: its `finalize` hides the loader once the data is in.
    if (this.isBootstrapping && !this.isLocalHost) {
      return;
    }
    this.isLoading.next(false);
  }

  /**
   * Hiding the loader swaps the router outlet back in, which rebuilds whatever route is current.
   * Mid-navigation that is the page being left — after a partner sign-in the login page, whose
   * form then flashes until the portal arrives — so the request waits for the router to settle.
   */
  private stopLoading(): void {
    if (this.isNavigationPending) {
      this.isLoaderReleaseDeferred = true;
      return;
    }
    this.isLoaderReleaseDeferred = false;
    this.isLoading.next(false);
  }

  private clearAllData(){
    this.isBootstrapping = false;
    this.permissionsService.clear();
    this.llmInstructionService.clear();
    this.messageTemplateService.clear();
    this.programTemplateService.clear();
    this.seriesTemplateService.clear();
    this.sessionService.clear();
    this.smartUserProgramService.clear();
    this.smartSurveyService.clear();
    this.sessionTemplateService.clear();
    this.buildingBlockTemplateService.clear();
    this.buildingBlockTagService.clear();
    this.placeholderService.clear();
    this.placeholderGroupService.clear();
    this.sessionCategoryService.clear();
    this.therapeuticDomainService.clear();
    this.voiceService.clear();
    this.categorizationService.clear();
    this.accentService.clear();
    this.countryService.clear();
    this.customerService.clear();
    this.adminStatsService.clear();
    this.partnerDashboardService.clear();
    this.sessionLengthStatsService.clear();
    this.userService.clear();
    this.partnerUserService.clear();
    this.practiceContextService.clear();
  }

  /**
   * Bootstrap order: permissions first, since every other load is gated on them; then the
   * partner profile for resellers; then whatever back-office stores the permissions allow.
   */
  private bootstrap(roles: UserRole[]): Observable<unknown> {
    return this.permissionsService.load().pipe(
      switchMap(() => this.loadCurrentUser(roles)),
      switchMap(user => {
        if(user){
          this.recordTimeZone(user);
          const loads = this.getAuthorizedBackOfficeLoads();
          console.debug(`Bootstrapping ${loads.length} back-office loads`);
          return loads.length ? this.runBackOfficeLoads(loads) : of([]);
        } else {
          console.error('Could not load user in bootstrap');
          return of([])
        }
      })
    );
  }

  /** Best effort: without it, emails write dates in the practice's country instead. */
  private recordTimeZone(user: SubriseUserDetails): void {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone === user.timeZone) return;
    this.partnerUserApiService.updateTimeZone(timeZone).subscribe({
      error: (error: unknown) => console.error('Could not record the time zone', error),
    });
  }

  /**
   * The shell's profile selector needs `currentUser` for every portal role, so it is loaded on a
   * request of its own — the paged `/users` list is no longer part of the bootstrap at all, and
   * is fetched by the clients screens that show it. A failure here must not take the rest of the
   * bootstrap down with it.
   *
   * A pure partner only has the partner endpoints. A partner who also holds a back-office role
   * (admin, editor, practitioner, …) is a back-office user: `/users/me` is what this resolves to,
   * and the partner load runs after it purely for its side effect — posting the Firebase sync the
   * partner endpoints require — so its own result and its failures are both discarded.
   */
  private loadCurrentUser(roles: UserRole[]): Observable<SubriseUserDetails|null> {
    if (this.isPartnerOnly(roles)) {
      return this.partnerUserService.loadPartnerCurrentUser();
    }

    const isPartner = roles.some((role) => PARTNER_ROLES.includes(role));
    return this.userService.loadCurrentUser().pipe(
      catchError(() => of(null)),
      switchMap((user) =>
        isPartner
          ? this.partnerUserService.loadPartnerCurrentUser().pipe(
              catchError(() => of(null)),
              map(() => user)
            )
          : of(user)
      )
    );
  }

  /**
   * A partner role and no other portal role. `EndUser` is ignored: it grants no portal access of
   * its own (see `rolesHavePortalAccess`), so a partner who also carries it is still partner-only.
   */
  private isPartnerOnly(roles: UserRole[]): boolean {
    return roles.some((role) => PARTNER_ROLES.includes(role))
      && roles.every((role) => PARTNER_ROLES.includes(role) || role === UserRole.EndUser);
  }

  private getAuthorizedBackOfficeLoads(): Array<() => Observable<unknown>> {
    return this.backOfficeLoaders
      .filter((loader) => this.permissionsService.has(loader.permission))
      .map((loader) => loader.load);
  }

  /**
   * Runs the loads a few at a time instead of all at once. Each one is also isolated: a store
   * that fails or times out records its own error and the bootstrap carries on, where a single
   * `forkJoin` would have torn down every sibling request still in flight.
   */
  private runBackOfficeLoads(loads: ReadonlyArray<() => Observable<unknown>>): Observable<unknown> {
    return from(loads).pipe(
      mergeMap(
        (load) =>
          load().pipe(
            catchError((error) => {
              console.error('Back-office load failed, continuing bootstrap', error);
              return of(null);
            })
          ),
        AppComponent.BACK_OFFICE_LOAD_CONCURRENCY
      ),
      toArray()
    );
  }

  private loadPlaceholders(): Observable<unknown> {
    return this.placeholderGroupService.load().pipe(
      switchMap(groups => {
        console.debug('Placeholder groups loaded', groups);
        const placeholderGroupIds = groups.map(group => group.id);
        return this.placeholderService.load(placeholderGroupIds);
      }),
      tap(placeholders => console.debug('Placeholders loaded', placeholders || []))
    );
  }
}
